
import React, { createContext, useContext, useState, useEffect } from "react";
import { CompanyBranding } from "@/lib/types";

interface BrandingContextType {
  branding: CompanyBranding;
  updateBranding: (brandingData: Partial<CompanyBranding>) => void;
}

// Default branding with new color palette
const defaultBranding: CompanyBranding = {
  id: "default",
  name: "F.F.V",
  companyName: "Future Financial Vision", // Added company name
  primaryColor: "#2ecc71", // Green
  secondaryColor: "#9F9EA1", // Silver
  accentColor: "#000000e6",  // Black
  fontFamily: "Inter, system-ui, sans-serif",
  logoUrl: undefined,
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<CompanyBranding>(defaultBranding);

  // Load branding on mount
  useEffect(() => {
    const savedBranding = localStorage.getItem("branding");
    if (savedBranding) {
      setBranding(JSON.parse(savedBranding));
    }
  }, []);

  // Apply CSS variables when branding changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Apply custom colors as CSS variables
    root.style.setProperty("--primary-color", branding.primaryColor);
    root.style.setProperty("--secondary-color", branding.secondaryColor);
    root.style.setProperty("--accent-color", branding.accentColor);
    
    // Apply custom font if specified
    if (branding.fontFamily) {
      root.style.setProperty("--font-family", branding.fontFamily);
    }
    
    // Apply any custom CSS
    if (branding.customCss) {
      const existingStyle = document.getElementById("custom-branding");
      if (existingStyle) {
        existingStyle.textContent = branding.customCss;
      } else {
        const style = document.createElement("style");
        style.id = "custom-branding";
        style.textContent = branding.customCss;
        document.head.appendChild(style);
      }
    }
  }, [branding]);

  const updateBranding = (brandingData: Partial<CompanyBranding>) => {
    const updatedBranding = { ...branding, ...brandingData };
    localStorage.setItem("branding", JSON.stringify(updatedBranding));
    setBranding(updatedBranding);
  };

  return (
    <BrandingContext.Provider
      value={{
        branding,
        updateBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};
